import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 transition-colors duration-300 font-medium inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Directory
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <article className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Terms of Use & Data Collection</h1>

          <p className="text-gray-600 text-sm mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using The Definitive D8 Directory (the "Service"), operated by Alpha Research,
                you accept and agree to be bound by these Terms of Use. If you do not agree to these terms,
                please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 leading-relaxed">
                The Definitive D8 Directory provides research-backed information and rankings of online
                vendors selling hemp-derived cannabinoid products. Our service is informational only and
                does not constitute an endorsement, recommendation, or guarantee of any vendor or product.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-3">By using our Service, you agree to:</p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Be at least 21 years of age</li>
                <li>Comply with all applicable local, state, and federal laws</li>
                <li>Verify the legal status of products in your jurisdiction before purchase</li>
                <li>Use the information provided for personal, non-commercial purposes</li>
                <li>Not misrepresent your identity or affiliation</li>
                <li>Not attempt to interfere with the Service's operation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Collection & Usage</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Information We Collect</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect limited information to provide and improve our Service:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>Automatically Collected Data:</strong> IP address, browser type, device information, pages viewed, referral sources</li>
                <li><strong>Cookies:</strong> Essential cookies for functionality, analytics cookies for usage patterns</li>
                <li><strong>Voluntary Information:</strong> Any information you provide through contact forms or communications</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">4.2 How We Use Data</h3>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Improve website performance and user experience</li>
                <li>Analyze traffic patterns and popular content</li>
                <li>Detect and prevent fraudulent activity</li>
                <li>Respond to user inquiries</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">4.3 Data Sharing</h3>
              <p className="text-gray-700 leading-relaxed">
                We do not sell your personal data. We may share aggregated, anonymized data with third parties
                for analytics purposes. We use Cloudflare for hosting, which processes data as described in
                their privacy policy.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">4.4 Data Retention</h3>
              <p className="text-gray-700 leading-relaxed">
                We retain automatically collected data for up to 90 days for analytics purposes. Voluntary
                communications are retained as long as necessary to respond to inquiries or as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on this Service, including text, graphics, research data, rankings, and methodology,
                is the property of Alpha Research and protected by copyright and intellectual property laws.
                You may not reproduce, distribute, or create derivative works without explicit permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Disclaimers</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.1 Information Accuracy</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we strive to provide accurate and current information, we make no warranties about
                the accuracy, completeness, or reliability of vendor information, rankings, or research data.
                Vendor information may change without notice.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.2 No Endorsement</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Inclusion in our directory does not constitute an endorsement. We are not responsible for
                the quality, safety, legality, or availability of products offered by listed vendors.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.3 Legal Compliance</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Users are solely responsible for ensuring compliance with all applicable laws in their
                jurisdiction. Hemp-derived cannabinoid laws vary by state and locality. Some products may
                be illegal in your area.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.4 "AS IS" Provision</h3>
              <p className="text-gray-700 leading-relaxed">
                This Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either
                express or implied, including but not limited to merchantability, fitness for a particular
                purpose, or non-infringement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the fullest extent permitted by law, Alpha Research and its affiliates shall not be
                liable for any indirect, incidental, special, consequential, or punitive damages arising
                from your use of the Service, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 mt-3 space-y-2">
                <li>Damages related to purchases from listed vendors</li>
                <li>Reliance on information provided in the directory</li>
                <li>Legal consequences from product possession or use</li>
                <li>Service interruptions or data loss</li>
                <li>Third-party actions or omissions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Service contains links to third-party websites and vendor sites. We are not responsible
                for the content, privacy practices, or terms of use of these external sites. Clicking these
                links is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Age Restriction</h2>
              <p className="text-gray-700 leading-relaxed">
                This Service is intended for users 21 years of age or older. By using this Service, you
                represent that you are at least 21 years old. We do not knowingly collect information from
                individuals under 21.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be posted on this page
                with an updated date. Continued use of the Service after changes constitutes acceptance of
                the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United
                States, without regard to conflict of law provisions. Any disputes shall be resolved in
                courts of competent jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these Terms or our data collection practices:
              </p>
              <p className="text-gray-700 mt-3">
                <strong>Alpha Research</strong><br />
                Email: <a href="mailto:leverage_labs_alpha@proton.me" className="text-blue-600 hover:underline">leverage_labs_alpha@proton.me</a>
              </p>
            </section>
          </div>

          {/* Back to Directory CTA */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-md hover:shadow-lg"
            >
              Back to Vendor Directory
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
